import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { INIT_CHART_OPTION_SMALL, KPI_CHARTS_WITH_INCREASED_BOTTOM_GRID } from '@catalogues';
import {
  INIT_KPI_CHART_OPTION_LARGE,
  INIT_KPI_CHART_OPTION_MEDIUM,
  INIT_KPI_CHART_OPTION_SMALL
} from '@catalogues';
import { ChartSize, KpiChartId } from '@models';
import { StudyFilterService } from '@services';
import { EChartsOption } from 'echarts';
import { ECharts } from 'echarts/core';

@Component({
  selector: 'chart',
  templateUrl: './chart.component.html',
  styleUrl: './chart.component.scss'
})
export class ChartComponent implements OnChanges {
  chartInstance!: ECharts;
  @Input() filterService: any = inject(StudyFilterService); // Default to StudyFilterService
  @Input() initOption:
    | typeof INIT_CHART_OPTION_SMALL
    | typeof INIT_KPI_CHART_OPTION_SMALL
    | typeof INIT_KPI_CHART_OPTION_MEDIUM
    | typeof INIT_KPI_CHART_OPTION_LARGE
    | any;
  @Input() chartTitle = '';
  @Input() sortedData!: string[];
  @Input() chartOption!: EChartsOption | any;
  @Input() colorMap?: Record<string, string>;
  @Input() expandedView?: boolean;
  @Input() showAllKeys = true;
  @Input() field = '';
  @Input() isKpiData = false;
  @Input() chartBaseTitle? = '';
  @Input() kpiXAxisLabel: string | undefined = '';
  @Input() enableScroll = false;
  @Input() scrollVisibleItems?: number;
  @Input() kpiAxisKeysShownDefault: number | undefined = undefined;
  @Input() kpiChartSize: ChartSize = 'medium';
  @Input() kpiOnTrack?: number;
  @Input() kpiOnTarget?: number;
  @Input() isPercent = false;
  @Input() kpiChartIdentifier = '';
  @Input() includeToggleInHeightCalculation = false;
  @Output() expandRequested = new EventEmitter<void>();

  public chartKeys: string[] = [];
  public Math = Math;
  render = true;
  /**
   * Keeping the array if in future requirements change and we need to add more chart identifiers
   * previously had:
   * KPI_CHARTS_WITH_NO_TARGET_LINE = ['barEnrolledByCountry', 'barEnrollmentByOrganization'];
   * see CRG-2918
   */
  KPI_CHARTS_WITH_NO_TARGET_LINE: string[] = [];
  KPI_CHARTS_WITH_NO_ON_TRACK_LINE = ['barEnrollmentByOrganization'];
  KPI_CHARTS_CAN_SHOW_LABELS = [
    'barEnrolledByOrganization',
    'barEnrolledByCountry',
    'barActiveByCountryToTarget',
    'barEnrollmentByCountry',
    'barSubjectScreenFailRateByCountry',
    'barSubjectScreenFailRateBySite',
    'barSubjectScreenFailRateByReason',
    'barEarlyTermByCountry',
    'barEarlyTermBySite',
    'barEarlyTermByReason',
    'barCrfReviewCompletedByCountry',
    'barCrfReviewCompletedBySite',
    'barOnTimeDispatchClinicalMaterialsBySite',
    'barSdvComplianceByCountry',
    'barSdvComplianceBySite'
  ];
  KPI_CHARTS_WITH_NUMERATOR_AND_DENOMINATOR_LABELS = [
    'barEnrolledByCountry',
    'barEnrollmentByCountry',
    'barSubjectScreenFailRateByCountry',
    'barSubjectScreenFailRateBySite',
    'barSubjectScreenFailRateByReason',
    'barEarlyTermByCountry',
    'barEarlyTermBySite',
    'barEarlyTermByReason',
    'barCrfReviewCompletedByCountry',
    'barCrfReviewCompletedBySite',
    'barOnTimeDispatchClinicalMaterialsBySite',
    'barSdvComplianceByCountry',
    'barSdvComplianceBySite'
  ];

  private cdr = inject(ChangeDetectorRef);

  onChartInit(chart: ECharts) {
    this.chartInstance = chart;
    chart.resize();
  }

  onExpandButtonClick() {
    this.expandRequested.emit();
  }

  onChartClick(field: string, event: any) {
    if (event && event.name && field !== '') {
      this.filterService.toggleFilter(field, event.name, 'chart');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const changesArg: any = changes;
    if (changesArg && changesArg['initOption'] && this.chartInstance) {
      this.chartInstance.dispose();
      this.render = false;
      this.cdr.detectChanges();
      this.render = true;
    }

    this.updateChartOptions();
  }

  // Keep an eye on this method to try to reduce complexity
  private updateChartOptions() {
    if (this.chartOption && this.sortedData) {
      let keys = Object.keys(this.sortedData).filter((k) => k !== '');
      keys = this.getSlicedKeys(keys);
      this.chartKeys = keys;

      const seriesArr = Array.isArray(this.chartOption.series)
        ? this.chartOption.series
        : [this.chartOption.series];
      const chartType = seriesArr[0]?.type;

      if (chartType === 'bar') {
        const grid = this.chartOption.grid ? { ...this.chartOption.grid } : {};
        this.setKpiChartGrid(grid);
        const xAxis = this.chartOption.xAxis ? { ...this.chartOption.xAxis } : {};
        this.setKpiChartAxisStyle(xAxis);
        const yAxis = this.chartOption.yAxis ? { ...this.chartOption.yAxis } : {};
        this.setKpiChartAxisStyle(yAxis);
        const dataZoom = this.setChartDataZoom(keys);

        const showLabels = this.canShowKpiLabels();
        let seriesConfig = {
          label: {
            show: showLabels,
            position: 'right',
            formatter: (params: any) => {
              const data = (this.sortedData as any)[params.name];
              if (
                data &&
                typeof data === 'object' &&
                'numerator' in data &&
                'denominator' in data
              ) {
                return this.formatKpiLabel(data);
              }
              return '';
            }
          },
          ...(seriesArr[0] ?? {}),
          data: this.getDataValues(keys)
        };

        this.setYAxisTruncation(yAxis);
        this.setXAxisMax(xAxis, keys);
        this.setChartHeight(grid);
        this.setKpiAxisName(xAxis);

        this.setDataKeysForAxis(xAxis, keys);
        this.setDataKeysForAxis(yAxis, keys);

        seriesConfig = this.buildOnTrackLine(seriesConfig);

        this.chartOption = {
          ...this.chartOption,
          grid,
          xAxis,
          yAxis,
          series: [seriesConfig],
          dataZoom: dataZoom
        };
      } else if (chartType === 'pie') {
        this.chartOption = {
          ...this.chartOption,
          title: {
            ...this.chartOption.title
          },
          series: [
            {
              ...(seriesArr[0] ?? {}),
              data: this.getDataValuesforPie(keys)
            }
          ]
        };
        if (this.isKpiData) {
          this.chartOption.title.text =
            this.chartBaseTitle && this.kpiChartSize !== 'small' ? this.chartBaseTitle : '';
        }
      }
    }
  }

  private validateAxisHasCategory(axis: any): boolean {
    if (typeof axis === 'object' && axis && 'type' in axis && (axis as any).type === 'category') {
      return true;
    }
    return false;
  }

  private setDataKeysForAxis(axis: any, keys: string[]) {
    if (this.validateAxisHasCategory(axis)) {
      (axis as any).data = keys;
    }
  }
  private formatKpiLabel(data: any) {
    if (this.kpiChartIdentifier === 'barActiveByCountryToTarget') {
      return this.formatKPILabelForbarActiveByCountryToTarget(data);
    } else if (
      this.KPI_CHARTS_WITH_NUMERATOR_AND_DENOMINATOR_LABELS.includes(this.kpiChartIdentifier)
    ) {
      return data.value === 0 ? '-- of --' : `${data.numerator} of ${data.denominator}`;
    } else {
      return data.value === 0 ? '--' : `${data.value}`;
    }
  }

  private formatKPILabelForbarActiveByCountryToTarget(data: any) {
    // no need to return label for small size
    if (this.kpiChartSize === 'small') {
      return '';
    }
    return data.value === 0 ? '-- of --' : `${data.numerator} of ${data.denominator}`;
  }

  getSortedDataKeysLength(): number {
    return this.sortedData ? Object.keys(this.sortedData).length : 0;
  }

  private setKpiChartGrid(grid: any) {
    if (this.isKpiData && (this.kpiChartSize === 'small' || this.kpiChartSize === 'medium')) {
      if (this.kpiChartSize === 'medium') {
        grid.left = '5%';
        if (this.canShowKpiLabels()) {
          grid.right = '10%';
        } else {
          grid.right = '5%';
        }
      }
      grid.top = '5%';
      grid.bottom = '10%';
    }
  }

  private setKpiChartAxisStyle(axis: any) {
    let fontSize = 12;
    let fontWeight = 'normal';
    let nameFontWeight = 'normal';
    if (!this.isKpiData) {
      return;
    }
    if (this.kpiChartSize === 'large') {
      fontSize = 12;
      fontWeight = 'normal';
      nameFontWeight = 'bold';
    } else if (this.kpiChartSize === 'medium') {
      nameFontWeight = 'bold';
    }

    if (this.kpiChartSize === 'medium' || this.kpiChartSize === 'large') {
      axis.axisLabel = {
        ...axis.axisLabel,
        color: '#1B1B1D',
        fontSize: fontSize,
        fontWeight: fontWeight,
        fontFamily: 'HelveticaNeue, Helvetica, Arial, sans-serif'
      };
      axis.nameTextStyle = {
        ...axis.nameTextStyle,
        color: '#1B1B1D',
        fontSize: fontSize,
        fontWeight: nameFontWeight,
        fontFamily: 'HelveticaNeue, Helvetica, Arial, sans-serif'
      };
    }
  }

  private setYAxisTruncation(yAxis: any) {
    if (this.isKpiData) {
      switch (this.kpiChartSize) {
        case 'small':
          yAxis.axisLabel = { ...yAxis.axisLabel, overflow: 'truncate', margin: 10, width: 100 };
          break;
        case 'medium':
          yAxis.axisLabel = { ...yAxis.axisLabel, overflow: 'truncate', margin: 10, width: 250 };
          break;
        case 'large':
          yAxis.axisLabel = { ...yAxis.axisLabel, overflow: 'truncate', margin: 10, width: 650 };
          break;
      }
    }
  }

  private setChartDataZoom(keys: string[]): any[] | undefined {
    if (!this.enableScroll || !this.scrollVisibleItems || keys.length <= this.scrollVisibleItems) {
      return undefined;
    }

    const startPercentage = 100 - (this.scrollVisibleItems / keys.length) * 100;

    return [
      {
        type: 'slider',
        yAxisIndex: 0,
        start: startPercentage,
        end: 100,
        width: 8,
        right: 15,
        handleSize: 0,
        showDetail: false,
        showDataShadow: false,
        brushSelect: false,
        borderColor: 'transparent',
        fillerColor: '#aaa',
        backgroundColor: '#f0f0f0',
        zoomLock: true
      },
      {
        type: 'inside',
        yAxisIndex: 0,
        start: startPercentage,
        end: 100,
        zoomOnMouseWheel: false,
        moveOnMouseWheel: true,
        moveOnMouseMove: false,
        throttle: 0
      }
    ];
  }

  private setXAxisMax(xAxis: any, keys: string[]) {
    if (this.isPercent) {
      if (this.chartKeys.length > 0) {
        const valuesArray = this.chartKeys.map((k) => (this.sortedData as any)[k]);
        const maxValue = Math.max(...valuesArray.map((d: any) => (d.value ? d.value : 0)));
        if (maxValue <= 100) {
          xAxis.max = 100;
        } else if (maxValue > 100) {
          xAxis.max = maxValue + 20;
        }
      } else {
        xAxis.max = 100;
      }
    } else if (this.enableScroll && this.scrollVisibleItems) {
      const allValues = keys.map((k: string) =>
        !this.isKpiData ? (this.sortedData as any)[k].length : (this.sortedData as any)[k].value
      );
      const maxValue = Math.max(...allValues);
      xAxis.max = maxValue;
      xAxis.min = 0;
    }
  }

  private canShowKpiLabels() {
    return (
      this.isKpiData &&
      this.KPI_CHARTS_CAN_SHOW_LABELS.includes(this.kpiChartIdentifier) &&
      this.kpiChartSize !== 'small'
    );
  }

  private buildOnTrackLine(seriesConfig: any) {
    const markLineData = [];
    if (
      this.kpiOnTrack !== undefined &&
      !this.KPI_CHARTS_WITH_NO_ON_TRACK_LINE.includes(this.kpiChartIdentifier)
    ) {
      const onTrackLabel =
        this.kpiChartSize === 'small' ? 'On track' : 'On track: ' + this.kpiOnTrack + '%';

      markLineData.push({
        xAxis: this.kpiOnTrack,
        lineStyle: { color: '#F35200', type: 'dashed' },
        label: { show: true, formatter: onTrackLabel, position: 'end', offset: [0, 15] }
      });
    }
    if (
      this.kpiOnTarget !== undefined &&
      !this.KPI_CHARTS_WITH_NO_TARGET_LINE.includes(this.kpiChartIdentifier)
    ) {
      const onTargetLabel =
        this.kpiChartSize === 'small' ? 'Target' : 'Target: ' + this.kpiOnTarget + '%';
      markLineData.push({
        xAxis: this.kpiOnTarget,
        lineStyle: { color: '#F35200', type: 'solid' },
        label: { show: true, formatter: onTargetLabel, position: 'end', offset: [0, 30] }
      });
    }
    if (markLineData.length) {
      seriesConfig = {
        ...seriesConfig,
        markLine: {
          symbol: 'none',
          data: markLineData
        }
      };
    }
    return seriesConfig;
  }

  private setKpiAxisName(xAxis: any) {
    if (
      this.isKpiData &&
      this.kpiChartSize !== 'small' &&
      typeof xAxis === 'object' &&
      xAxis &&
      'name' in xAxis &&
      this.kpiXAxisLabel !== undefined
    ) {
      // set the xAxis name
      (xAxis as any).name = this.kpiXAxisLabel;
    } else if (this.isKpiData && this.kpiChartSize === 'small') {
      (xAxis as any).name = '';
    }
  }

  private getSlicedKeys(keys: string[]) {
    if (this.enableScroll && this.scrollVisibleItems) {
      return keys;
    }

    if (!this.showAllKeys) {
      if (this.isKpiData && this.kpiAxisKeysShownDefault !== undefined) {
        keys = keys.slice(-this.kpiAxisKeysShownDefault);
      } else {
        keys = keys.slice(-4);
      }
    }
    return keys;
  }

  private getDataValues(keys: string[]) {
    return !this.isKpiData
      ? keys.map((k: any) => (this.sortedData as any)[k].length)
      : keys.map((k: any) => (this.sortedData as any)[k].value);
  }

  private getDataValuesforPie(keys: string[]) {
    let auxCounter = 0;
    const colorMapLength = this.colorMap ? Object.values(this.colorMap).length : 0;
    return keys.map((k: any) => {
      const color = !this.isKpiData
        ? this.colorMap?.[k]
        : auxCounter < colorMapLength
          ? Object.values(this.colorMap!)[auxCounter]
          : undefined;
      auxCounter += 1;
      const baseItem = {
        name: k,
        value: this.isKpiData
          ? (this.sortedData as any)[k].value
          : (this.sortedData as any)[k].length
      };
      return color ? { ...baseItem, itemStyle: { color } } : baseItem;
    });
  }

  private setChartHeight(grid: any) {
    if (
      this.isKpiData &&
      this.showAllKeys &&
      this.kpiChartSize === 'medium' &&
      Object.keys(this.sortedData).length >= 5
    ) {
      this.initOption = { ...INIT_KPI_CHART_OPTION_MEDIUM };
      const chartSizeWithKeysLength = this.chartKeys.length * 30;
      const chartHeightWithToggle = 250;
      const chartHeightWithoutToggle = 270;
      const minHeight = this.includeToggleInHeightCalculation
        ? chartHeightWithToggle
        : chartHeightWithoutToggle;
      const increaseGridBottom = chartSizeWithKeysLength <= minHeight;

      this.initOption.height = Math.max(minHeight, chartSizeWithKeysLength);
      grid.top = '1%';
      if (KPI_CHARTS_WITH_INCREASED_BOTTOM_GRID.includes(this.kpiChartIdentifier as KpiChartId)) {
        if (increaseGridBottom) {
          grid.bottom = '18%';
        } else {
          grid.bottom = this.chartKeys.length >= 10 ? '10%' : '5%';
        }
      } else {
        grid.bottom = '2%';
      }
    }
  }
}
